import { useField } from 'formik';
import { TemplateTag } from '../../models';
import check from '../../assets/check.svg';
import cross from '../../assets/cross.svg';
import React, { useEffect, useState } from 'react';

function TemplateTagSearchItem({
  tagId,
  tagName,
  selected,
  isTagOptions,
  setValue,
  setSelectedTag,
  closeDropdown,
}: {
  tagId: number | null;
  tagName: string;
  selected: boolean;
  isTagOptions: boolean;
  setValue: (value: string) => void;
  setSelectedTag: (selectedTag: TemplateTag | undefined) => void;
  closeDropdown: () => void;
}) {
  const onSelect = () => {
    closeDropdown();
    setValue(tagName);
    setSelectedTag({
      id: tagId,
      name: tagName,
    });
  };

  const SelectedIcon = () => {
    return (
      <div className="w-[16px] h-[16px] px-[1px] pt-[3px] rounded aspect-square bg-green-200">
        <img alt="checked" src={check} />
      </div>
    );
  };

  const ExistedIcon = () => {
    return (
      <div className="w-[16px] h-[16px] px-[2px] pt-[2px] rounded aspect-square bg-gray-400">
        <div className="w-[12px] h-[12px] rounded-sm aspect-square bg-gray-400 ring-[#EDF2FF] ring-[1px]"></div>
      </div>
    );
  };

  const AddIcon = () => {
    return (
      <div className="w-[16px] h-[16px] px-[4px] pt-[4px] rounded aspect-square bg-gray-400">
        <img alt="added" src={cross} className="rotate-45 h-2 w-2" />
      </div>
    );
  };

  return (
    <li
      className="cursor-pointer w-full h-[40px] p-1 px-4 flex items-center rounded hover:bg-gray-100"
      onClick={onSelect}
    >
      {selected && <SelectedIcon />}
      {!selected && isTagOptions && <ExistedIcon />}
      {!selected && !isTagOptions && <AddIcon />}
      <span
        title={tagName}
        className="w-full ml-2 text-sm font-medium rounded text-ellipsis overflow-hidden whitespace-pre"
      >
        {tagName}
      </span>
    </li>
  );
}

function TemplateTagSearchDropdown({
  containedInList,
  value,
  templateTagFilteredList,
  selectedTag,
  setValue,
  setSelectedTag,
  closeDropdown,
}: {
  containedInList: boolean;
  value: string;
  templateTagFilteredList: TemplateTag[];
  selectedTag: TemplateTag | undefined;
  setValue: (value: string) => void;
  setSelectedTag: (selectedTag: TemplateTag | undefined) => void;
  closeDropdown: () => void;
}) {
  return (
    <ul className="absolute max-h-28 w-full rounded-md bg-white shadow-lg shadow-neutral-200 border mt-0.5 overflow-y-auto">
      {!containedInList && value.trim().length > 0 && (
        // User Typing Tag
        <TemplateTagSearchItem
          key={0}
          tagId={null}
          tagName={value.trim()}
          selected={selectedTag?.name === value.trim()}
          isTagOptions={false}
          setValue={setValue}
          setSelectedTag={setSelectedTag}
          closeDropdown={closeDropdown}
        />
      )}
      {templateTagFilteredList.map((tagFilteredOption) => (
        // Existed Tags & Selected Tag (Selected Tag on the top)
        <TemplateTagSearchItem
          key={tagFilteredOption.id}
          tagId={tagFilteredOption.id}
          tagName={tagFilteredOption.name}
          selected={tagFilteredOption.name === selectedTag?.name}
          isTagOptions={true}
          setValue={setValue}
          setSelectedTag={setSelectedTag}
          closeDropdown={closeDropdown}
        />
      ))}
    </ul>
  );
}

function DeleteButton({
  onClick,
}: {
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}) {
  return (
    <button
      className="absolute right-3 top-6 h-4 w-4 px-[4px] bg-red-500 hover:bg-red-600 cursor-pointer rounded"
      onClick={onClick}
      type="button"
    >
      <img alt="deleted" src={cross} className="h-2 w-2" />
    </button>
  );
}

export default function TemplateTagField({
  title,
  placeholder,
  tagOptions,
  templateTagId,
  ...attrs
}: {
  title: string;
  placeholder: string;
  tagOptions: TemplateTag[];
  templateTagId: number | null;
} & Record<string, any>) {
  const [field, meta, helpers] = useField(attrs.name);
  const { value } = meta;
  const { setValue } = helpers;
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [isInputFieldTyping, setInputFieldTyping] = useState<boolean>(false);
  const [containedInList, setContainedInList] = useState<boolean>(false);
  const [templateTagFilteredList, setTemplateTagFilteredList] =
    useState<TemplateTag[]>(tagOptions);
  const [selectedTag, setSelectedTag] = useState<TemplateTag | undefined>(
    typeof templateTagId === 'number'
      ? {
          id: templateTagId,
          name: value,
        }
      : undefined
  );

  useEffect(() => {
    let containedInList = false;
    const filtered: TemplateTag[] = [];
    if (!isInputFieldTyping) {
      // Display all existed tagOptions & selected tag before user start typing
      if (selectedTag) {
        containedInList = true;
        filtered.push(selectedTag);
      }
      tagOptions.forEach((tagOption: TemplateTag) => {
        if (tagOption.name !== selectedTag?.name) {
          filtered.push(tagOption);
        }
      });
    } else {
      // Filter Search Dropdown List when user start typing
      if (selectedTag) {
        containedInList = selectedTag.name === value.trim();
        filtered.push(selectedTag);
      }
      tagOptions.forEach((tagOption: TemplateTag) => {
        containedInList = containedInList || tagOption.name === value.trim();
        if (
          tagOption.name.includes(value.trim()) &&
          tagOption.name !== selectedTag?.name
        ) {
          filtered.push(tagOption);
        }
      });
    }
    setContainedInList(containedInList);
    setTemplateTagFilteredList(filtered);
  }, [selectedTag, tagOptions, value, isInputFieldTyping]);

  const closeDropdown = () => {
    setDropdownOpen(false);
    setInputFieldTyping(false);
  };

  const showDeleteButton = ({
    selectedTag,
    isDropdownOpen,
  }: {
    selectedTag: TemplateTag | undefined;
    isDropdownOpen: boolean;
  }) => {
    return selectedTag?.id !== undefined || selectedTag?.name || isDropdownOpen;
  };

  const showSearchDropdown = ({
    isDropdownOpen,
    value,
    templateTagFilteredList,
  }: {
    isDropdownOpen: boolean;
    value: string | undefined;
    templateTagFilteredList: TemplateTag[];
  }) => {
    // Display Search Dropdown only when user is typing or a tag is selected or tagOptions existed
    return (
      isDropdownOpen &&
      ((value && value.trim().length > 0) ||
        selectedTag !== undefined ||
        templateTagFilteredList.length > 0)
    );
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      if (value.trim().length > 0) {
        const tagId =
          tagOptions.find((tagOption) => tagOption.name === value.trim())?.id ||
          null;
        setSelectedTag({
          id: tagId,
          name: value.trim(),
        });
      } else {
        setSelectedTag(undefined);
      }
      setValue(value.trim());
      closeDropdown();
      e.currentTarget.blur();
    } else if (e.key === 'Escape') {
      setValue(selectedTag?.name || '');
      closeDropdown();
      e.currentTarget.blur();
    } else {
      setInputFieldTyping(true);
    }
  };

  const onClickDeleteTag = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    closeDropdown();
    setValue('');
    setSelectedTag(undefined);
  };

  return (
    <div className="w-full text-[#53586C] relative">
      <label className="font-medium">{title}</label>
      <div className="relative">
        <input
          className="h-10 w-full border border-[#53586C] rounded py-3 px-4 pr-10 placeholder:text-gray-700/70 mt-3"
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setDropdownOpen(true)}
          onKeyDown={onKeyDown}
          {...field}
          {...attrs}
        />
        {showDeleteButton({ selectedTag, isDropdownOpen }) && (
          <DeleteButton onClick={onClickDeleteTag} />
        )}
        {showSearchDropdown({
          isDropdownOpen,
          value,
          templateTagFilteredList,
        }) && (
          <TemplateTagSearchDropdown
            containedInList={containedInList}
            value={value}
            templateTagFilteredList={templateTagFilteredList}
            selectedTag={selectedTag}
            setValue={setValue}
            setSelectedTag={setSelectedTag}
            closeDropdown={closeDropdown}
          />
        )}
      </div>
    </div>
  );
}
